import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2, MessageCircle, Send } from "lucide-react";
import { SOCIALS, WHATSAPP } from "@/lib/brand";
import { contactRequestsApi } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
    trackContactClick,
    trackFormSubmit,
} from "@/lib/analytics";
import { fadeUp, staggerContainer, blurClear } from "@/lib/animations";

const MegaCTA = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        contact: "",
        idea: "",
    });

    const whatsappUrl = `${WHATSAPP.sy.url}?text=${encodeURIComponent(
        t("common.whatsapp_initial_project"),
    )}`;

    const reassurance = [
        t("mega_cta.reassurance.r1"),
        t("mega_cta.reassurance.r2"),
        t("mega_cta.reassurance.r3"),
        t("mega_cta.reassurance.r4"),
    ];

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.contact || !formData.idea) {
            trackFormSubmit("contact", "validation_error", { service: "mega_cta_quick" });
            toast({
                title: t("contact.toast.validation_title"),
                description: t("contact.toast.validation_desc"),
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await contactRequestsApi.submit({
                name: formData.name,
                email: formData.contact,
                service: "Quick idea (Mega CTA)",
                message: formData.idea,
            });
            setIsSubmitted(true);
            trackFormSubmit("contact", "success", { service: "mega_cta_quick" });
            toast({
                title: t("contact.toast.success_title"),
                description: t("contact.toast.success_desc"),
            });
        } catch (error) {
            trackFormSubmit("contact", "error", { service: "mega_cta_quick" });
            toast({
                title: t("contact.toast.error_title"),
                description: t("contact.toast.error_desc"),
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            className="py-12 sm:py-16 md:py-24 lg:py-28"
            aria-labelledby="cta-heading"
        >
            <div className="container mx-auto px-5 sm:px-6">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={staggerContainer(0.12, 0.05)}
                    className="border border-white/15 bg-white/[0.02] p-6 sm:p-8 md:p-12 lg:p-16 relative overflow-hidden"
                >
                    {/* Subtle neon edge sweeps on the corners */}
                    <span
                        aria-hidden
                        className="absolute top-0 inset-x-0 h-px"
                        style={{ background: "var(--gradient-accent-v2)", opacity: 0.6 }}
                    />
                    <span
                        aria-hidden
                        className="absolute bottom-0 inset-x-0 h-px"
                        style={{ background: "var(--gradient-accent-v2)", opacity: 0.3 }}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                        {/* Left rail — eyebrow + title + reassurance */}
                        <div className="lg:col-span-6">
                            <div className="flex items-center gap-3 mb-6">
                                <span aria-hidden className="brutal-glyph" style={{ height: "0.65em" }} />
                                <span className="font-mono-brutal text-[11px] text-white/60">
                                    {t("blog_home.eyebrow", { defaultValue: "TALK" })} · CONTACT
                                </span>
                            </div>

                            <motion.h2
                                id="cta-heading"
                                variants={blurClear}
                                className="font-v2-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] rtl:leading-[1.2] mb-5 sm:mb-6"
                            >
                                <span className="block">{t("mega_cta.title_line_1")}</span>
                                <span
                                    className="block"
                                    style={{
                                        backgroundImage: "var(--gradient-accent-v2)",
                                        WebkitBackgroundClip: "text",
                                        backgroundClip: "text",
                                        color: "transparent",
                                    }}
                                >
                                    {t("mega_cta.title_line_2")}
                                </span>
                            </motion.h2>

                            <motion.p
                                variants={fadeUp}
                                className="text-base sm:text-lg text-white/65 leading-relaxed mb-8"
                            >
                                {t("mega_cta.subtitle")}
                            </motion.p>

                            <motion.ul
                                variants={fadeUp}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-8 lg:mb-0"
                            >
                                {reassurance.map((line) => (
                                    <li key={line} className="flex items-start gap-2.5 text-sm text-white/70">
                                        <CheckCircle2 className="w-4 h-4 mt-0.5 text-success shrink-0" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </motion.ul>
                        </div>

                        {/* Right rail — brutalist form */}
                        <div className="lg:col-span-6">
                            <AnimatePresence mode="wait">
                                {!isSubmitted ? (
                                    <motion.form
                                        key="form"
                                        onSubmit={handleSubmit}
                                        variants={staggerContainer(0.06, 0.05)}
                                        initial="hidden"
                                        animate="show"
                                        exit={{ opacity: 0 }}
                                        className="space-y-4"
                                    >
                                        <motion.input
                                            variants={fadeUp}
                                            required
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder={t("mega_cta.form.name_placeholder")}
                                            className="brutal-input w-full"
                                        />
                                        <motion.input
                                            variants={fadeUp}
                                            required
                                            type="text"
                                            value={formData.contact}
                                            onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                                            placeholder={t("mega_cta.form.contact_placeholder")}
                                            className="brutal-input w-full"
                                        />
                                        <motion.textarea
                                            variants={fadeUp}
                                            required
                                            rows={4}
                                            value={formData.idea}
                                            onChange={(e) => setFormData({ ...formData, idea: e.target.value })}
                                            placeholder={t("mega_cta.form.idea_placeholder")}
                                            className="brutal-input w-full resize-none"
                                        />
                                        <motion.div variants={fadeUp}>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="brutal-btn brutal-btn-primary w-full py-4 text-sm disabled:opacity-60"
                                            >
                                                {isSubmitting ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Send className="w-4 h-4" />
                                                        {t("mega_cta.form.submit")}
                                                        <ArrowUpRight className="w-4 h-4" />
                                                    </>
                                                )}
                                            </button>
                                        </motion.div>

                                        <motion.div
                                            variants={fadeUp}
                                            className="flex items-center gap-3 pt-2"
                                        >
                                            <span className="font-mono-brutal text-[10px] text-white/40 whitespace-nowrap">
                                                {t("mega_cta.form.or_label")}
                                            </span>
                                            <span aria-hidden className="flex-1 h-px bg-white/10" />
                                        </motion.div>

                                        <motion.div
                                            variants={fadeUp}
                                            className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10"
                                        >
                                            <a
                                                href={whatsappUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-cursor="hover"
                                                onClick={() => trackContactClick("whatsapp_sy", "home_mega_cta")}
                                                className="brutal-btn justify-center py-4 bg-background hover:bg-white/[0.04]"
                                            >
                                                <MessageCircle className="w-4 h-4" />
                                                {t("mega_cta.whatsapp")}
                                            </a>
                                            <a
                                                href={SOCIALS.telegram.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-cursor="hover"
                                                onClick={() => trackContactClick("telegram", "home_mega_cta")}
                                                className="brutal-btn justify-center py-4 bg-background hover:bg-white/[0.04]"
                                            >
                                                {t("mega_cta.telegram")}
                                            </a>
                                        </motion.div>
                                    </motion.form>
                                ) : (
                                    <motion.div
                                        key="success"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="border border-white/15 p-8 flex flex-col items-start gap-4 bg-white/[0.02]"
                                    >
                                        <span aria-hidden className="brutal-glyph" style={{ height: "0.7em" }} />
                                        <div className="flex items-center gap-3">
                                            <CheckCircle2 className="w-6 h-6 text-success" />
                                            <span className="font-mono-brutal text-[11px] text-success">
                                                REQUEST RECEIVED
                                            </span>
                                        </div>
                                        <h3 className="font-v2-display text-2xl sm:text-3xl text-white leading-tight">
                                            {t("contact.success_title")}
                                        </h3>
                                        <p className="text-sm text-white/65 leading-relaxed">
                                            {t("contact.success_body")}
                                        </p>
                                        <button
                                            onClick={() => {
                                                setIsSubmitted(false);
                                                setFormData({ name: "", contact: "", idea: "" });
                                            }}
                                            className="brutal-btn mt-2"
                                        >
                                            {t("contact.success_again")}
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default MegaCTA;
